import React from "react";
import { usePolling, useNow } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtUsd, shortAddr, countdown } from "@/lib/format";
import api from "@/lib/api";
import { CHAIN } from "@/contracts/croc";
import { Trophy, Timer, Crown, Medal, Radio, ExternalLink, Baby } from "lucide-react";

const MEDALS = ["#1CFD4C", "#C0C0C0", "#CD7F32"];

export default function Pools() {
  const { data } = usePolling(() => api.pools(), [], 20000);
  useNow(1000);
  const cd = data ? countdown(data.next_snapshot) : { h: "00", m: "00", sec: "00" };

  return (
    <div>
      <SectionTitle
        eyebrow="Croc Pool"
        title="Croc Reward Pool"
        subtitle="5% of company turnover funds one pool. Each day it pays 30% of its total — King Croc (highest team volume today) takes 60%, Baby Croc (highest self top-up today) takes 40% — and 70% carries forward."
        right={
          <div className="flex items-center gap-2 rounded-xl border border-[#1CFD4C]/30 bg-[#0F1712] px-4 py-2.5" data-testid="pool-countdown">
            <Timer className="w-4 h-4 gold-text" />
            <span className="font-num font-bold text-white">{cd.h}:{cd.m}:{cd.sec}</span>
          </div>
        }
        testid="pools-title"
      />

      <CrocPoolCard pool={data?.croc_pool} />

      <Panel data-testid="pools-leaderboard">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Medal className="w-4 h-4 gold-text" />
            <span className="font-display font-bold text-white">Global Leaderboard</span>
          </div>
          {data?.source === "chain" && (
            <Pill tone="emerald" data-testid="pools-onchain-badge">
              <Radio className="w-3 h-3" /> Live on-chain
            </Pill>
          )}
        </div>
        <div className="space-y-1.5">
          {(() => {
            const winnerAddrs = new Set((data?.croc_pool?.winners ?? []).map((w) => (w.address || "").toLowerCase()).filter(Boolean));
            const seen = new Set();
            return (data?.leaderboard ?? [])
              .filter((row) => {
                const a = (row.address || "").toLowerCase();
                if (!a || seen.has(a) || winnerAddrs.has(a)) return false; // dedupe + hide King/Baby (shown above)
                seen.add(a);
                return true;
              })
              .map((row) => (
            <a
              key={row.address}
              href={`${CHAIN.explorer}/address/${row.address}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3 hover:border-[#1CFD4C]/40 transition-colors group"
              data-testid={`leaderboard-row-${row.rank}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-lg grid place-items-center font-num text-sm" style={{ color: MEDALS[row.rank - 1] || "#9CA3AF", background: "rgba(255,255,255,0.04)" }}>{row.rank}</span>
                <div>
                  <div className="font-num text-white flex items-center gap-1.5">{shortAddr(row.address)}<ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                  <div className="text-[10px] text-muted-foreground">{row.badge}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-num text-white">{fmtUsd(row.team_volume)}</div>
                <div className="text-[10px] text-muted-foreground">team volume</div>
              </div>
            </a>
              ));
          })()}
          {(data?.leaderboard?.length ?? 0) === 0 && (
            <div className="text-center py-10 text-muted-foreground">No contestants yet.</div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function CrocPoolCard({ pool }) {
  const winners = pool?.winners ?? [];
  const king = winners.find((w) => w.title === "King Croc") || winners[0];
  const baby = winners.find((w) => w.title === "Baby Croc") || winners[1];
  return (
    <Panel glow="gold" className="mb-6" data-testid="croc-pool">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 gold-text" />
          <span className="font-display font-bold text-white text-lg">Croc Pool</span>
        </div>
        <Pill tone="emerald">30% today · 70% forward</Pill>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Mini label="Pool Total" value={fmtUsd(pool?.pool_total)} />
        <Mini label="Distributing Today" value={fmtUsd(pool?.distribute_today)} accent />
        <Mini label="Carryforward" value={fmtUsd(pool?.carryforward)} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <WinnerTile icon={Crown} title="King Croc" sub={king?.reason || "Highest team volume today"} w={king} big testid="croc-pool-king" />
        <WinnerTile icon={Baby} title="Baby Croc" sub={baby?.reason || "Highest self top-up today"} w={baby} testid="croc-pool-baby" />
      </div>
    </Panel>
  );
}

function WinnerTile({ icon: Icon, title, sub, w, big, testid }) {
  return (
    <div className="rounded-xl border border-[#1CFD4C]/30 bg-[#090E0B] p-5 text-center" data-testid={testid}>
      <div className="flex items-center justify-center gap-2 mb-2">
        <Icon className="w-4 h-4 gold-text" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</span>
      </div>
      <div className="font-num text-white text-sm">{w?.address ? shortAddr(w.address) : "—"}</div>
      <div className={`font-num font-black ${big ? "text-2xl" : "text-xl"} gold-text mt-1`}>{fmtUsd(w?.prize)}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#090E0B] p-3">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-num font-bold text-sm mt-1 ${accent ? "emerald-text" : "text-white"}`}>{value}</div>
    </div>
  );
}
